export function cleanText(text) {
  if (!text || text.trim().length === 0) {
    return '';
  }
  
  let cleaned = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  
  // Fix common OCR symbol errors
  cleaned = cleaned.replace(/[¢©®™°±×÷]/g, '');
  cleaned = cleaned.replace(/[^\w\s:().,\-%/]/g, (match) => {
    if (/[A-Za-z0-9:().,\-%/\s]/.test(match)) return match;
    return ' ';
  });
  
  // Fix missing first letters in common medical terms
  const medicalTermFixes = {
    'nemia': 'Anemia',
    'nxiety': 'Anxiety',
    'rthritis': 'Arthritis',
    'sthma': 'Asthma',
    'lcohol': 'Alcohol',
    'epression': 'Depression',
    'iabetes': 'Diabetes',
    'isease': 'Disease',
    'isorder': 'Disorder',
    'ypertension': 'Hypertension',
    'ypothyroidism': 'Hypothyroidism',
    'eoplasm': 'Neoplasm',
    'steopenia': 'Osteopenia',
    'steoporosis': 'Osteoporosis',
    'rediabetes': 'Prediabetes',
    'eflux': 'Reflux',
    'yndrome': 'Syndrome'
  };
  
  for (const [wrong, correct] of Object.entries(medicalTermFixes)) {
    cleaned = cleaned.replace(new RegExp(`(:\\s*)${wrong}\\b`, 'gi'), `$1${correct}`);
    cleaned = cleaned.replace(new RegExp(`(^|\\n)\\s*${wrong}\\b`, 'gi'), `$1${correct}`);
    cleaned = cleaned.replace(new RegExp(`(\\s)${wrong}\\b`, 'gi'), `$1${correct}`);
  }
  
  // Fix concatenated words by adding spaces before ICD codes
  cleaned = cleaned.replace(/([a-z0-9,)])([A-Z]\d{2})/g, '$1 $2');
  cleaned = cleaned.replace(/([a-z])([A-Z][a-z])/g, '$1 $2');
  cleaned = cleaned.replace(/([A-Z]\d{2}):\s+(\d)/g, '$1.$2');
  cleaned = cleaned.replace(/(disease|disorder|syndrome|deficiency|neoplasm)(with|without|of|to)/gi, '$1 $2');
  cleaned = cleaned.replace(/\bchronic\s+K([a-z])/gi, 'chronic k$1');
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  
  const lines = cleaned.split('\n');
  const processedLines = lines.map(line => line.trim());
  
  return processedLines.join('\n');
}

/**
 * Fix common OCR errors in ICD codes
 */
export function fixOCRErrorsInCodes(text) {
  let fixed = text;
  
  fixed = fixed.replace(/(ICD10-?|\(ICD10-?)([12]\d{2}(?:\.\d{1,4})?)/gi, (match, prefix, code) => {
    let fixedCode = code;
    if (code.startsWith('1')) {
      fixedCode = 'I' + code.substring(1);
    } else if (code.startsWith('2')) {
      fixedCode = 'Z' + code.substring(1);
    }
    return prefix + fixedCode;
  });
  
  fixed = fixed.replace(/(ICD10-?|\(ICD10-?)(2\d{2}(?:\.\d{1,4})?)/gi, (match, prefix, code) => {
    return prefix + 'Z' + code.substring(1);
  });
  
  fixed = fixed.replace(/\bESS\.(\d+)/g, 'E03.$1');
  fixed = fixed.replace(/ICD10-?V(\d{2}(?:\.\d{1,4})?)/gi, (match, code) => {
    return 'ICD-V' + code;
  });
  
  return fixed;
}

/**
 * Validate and fix ICD codes
 */
export function validateAndFixICDCode(code, isICD10 = true) {
  if (!code) return null;
  
  code = code.trim().replace(/[)\]\s]+$/, '');
  
  if (isICD10) {
    if (/^[12]\d{2}(?:\.\d{1,4})?$/.test(code)) {
      if (code.startsWith('1')) {
        code = 'I' + code.substring(1);
      } else if (code.startsWith('2')) {
        code = 'Z' + code.substring(1);
      }
    }
    
    if (/^[A-Z]\d{2}(?:\.\d{1,4})?$/.test(code)) {
      if (code.startsWith('V')) {
        return null;
      }
      return code;
    }
  } else {
    if (/^\d{3}(?:\.\d{1,4})?$/.test(code) || /^V\d{2}(?:\.\d{1,4})?$/.test(code)) {
      return code;
    }
  }
  
  return null;
}

/**
 * Extract problem list format from text
 */
export function extractProblemListFormat(text) {
  const problems = [];
  const lines = text.split('\n');
  
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i].trim();
    if (!line) continue;
    
    let fullLine = line;
    for (let j = 1; j <= 3 && i + j < lines.length; j++) {
      const nextLine = lines[i + j].trim();
      const hasCodes = nextLine.match(/[\(\[].*[ICD1CD0CD].*[\dA-Z]/i);
      const hasDescription = /^[A-Z][a-z]/.test(nextLine) && !nextLine.match(/[\(\[].*ICD/i);
      
      if (hasCodes && !fullLine.match(/[\(\[].*[ICD1CD0CD].*[\dA-Z]/i)) {
        fullLine = fullLine + ' ' + nextLine;
        break;
      } else if (hasDescription && !fullLine.match(/[\(\[].*ICD/i) && fullLine.length < 100) {
        fullLine = fullLine + ' ' + nextLine;
        continue;
      } else if (fullLine.match(/[\(\[].*ICD/i)) {
        break;
      }
    }
    
    fullLine = fullLine.replace(/([\(\[])1CD/gi, '$1ICD');
    fullLine = fullLine.replace(/ICD1[0O]/gi, 'ICD10');
    
    const pattern1 = /^(.+?)\s+(?:with\s+codes?\s+)?\(ICD-?(\d{3}(?:\.\d{1,4})?)\)\s*(?:and\s+)?\(ICD10-?([A-Z0-9]\d{2}(?:\.\d{1,4})?)\)/i;
    let match = fullLine.match(pattern1);
    
    if (!match) {
      const pattern2 = /^(.+?)\s+\(ICD-?(\d{3}(?:\.\d{1,4})?)\)\s*(?:and\s+)?\(ICD10-?([A-Z0-9]\d{2}(?:\.\d{1,4})?)\)/i;
      match = fullLine.match(pattern2);
    }
    
    if (!match) {
      const pattern2b = /^(.+?)[\s\(\[]+ICD-?(\d{3}(?:\.\d{1,4})?)[\)\]]?\s*(?:and\s+)?[\(\[]?ICD10-?([A-Z0-9]\d{2}(?:\.\d{1,4})?)[\)\]]?/i;
      match = fullLine.match(pattern2b);
    }
    
    if (!match) {
      const pattern3 = /^(.+?)\s+\(ICD10-?([A-Z0-9]\d{2}(?:\.\d{1,4})?)\)/i;
      match = fullLine.match(pattern3);
      if (match) {
        match = [match[0], match[1], '', match[2]];
      }
    }
    
    if (!match) {
      const pattern4 = /^(.+?)\s+\(ICD-?(\d{3}(?:\.\d{1,4})?)\)/i;
      match = fullLine.match(pattern4);
      if (match) {
        match = [match[0], match[1], match[2], ''];
      }
    }
    
    if (match && match[3]) {
      let icd10Code = match[3].replace(/[)\]\s]+$/, '');
      if (/^[120]/.test(icd10Code)) {
        icd10Code = icd10Code.replace(/^1/, 'I').replace(/^0/, 'O').replace(/^2/, 'Z');
      }
      icd10Code = validateAndFixICDCode(icd10Code, true);
      if (icd10Code) {
        match[3] = icd10Code;
      } else {
        match = null;
      }
    }
    
    if (match && match[2]) {
      let icd9Code = match[2].trim().replace(/[)\]\s]+$/, '');
      icd9Code = validateAndFixICDCode(icd9Code, false);
      if (icd9Code) {
        match[2] = icd9Code;
      }
    }
    
    if (match) {
      let description = match[1].trim();
      const icd9 = match[2] ? match[2].trim() : '';
      const icd10 = match[3] ? match[3].trim() : '';
      
      description = description.replace(/\s+with\s+codes?.*$/i, '');
      description = description.replace(/\s+\(ICD.*$/i, '');
      description = description.replace(/([a-z])([A-Z])/g, '$1 $2');
      
      if (description.length < 3) continue;
      if (/^[-)\]]/.test(description) || /^[)\]]\s*$/.test(description)) continue;
      if (/^[A-Z]?\d{2,3}(?:\.\d{1,4})?[)\]]?$/.test(description)) continue;
      if (/^[)\]\s-]+[A-Z]?\d/.test(description)) continue;
      if (/^[-)\]]+\s*[A-Z]?\d/.test(description)) continue;
      if (!/[A-Za-z]{3,}/.test(description)) continue;
      
      problems.push({
        description: description,
        icd9: icd9,
        icd10: icd10
      });
    }
  }
  
  return problems;
}

/**
 * Extract all ICD-10 codes from text (comprehensive extraction)
 */
function extractAllICD10Codes(text) {
  const codes = new Set();
  
  // Normalize text for better extraction - do this FIRST
  let normalizedText = text;
  
  // Fix OCR errors in ICD10- prefix format: ICD10-110 -> ICD10-I10
  normalizedText = normalizedText.replace(/\(?ICD[- ]?10[- ]?CM?[- ]?-([120])(\d{2}(?:\.\d{1,4})?)\)?/gi, (match, prefix, rest) => {
    let fixedPrefix = prefix;
    if (prefix === '1') fixedPrefix = 'I';
    else if (prefix === '0') fixedPrefix = 'O';
    else if (prefix === '2') fixedPrefix = 'Z';
    return match.replace(prefix + rest, fixedPrefix + rest);
  });
  
  // Fix common OCR errors: replace 1 with I, 0 with O, 2 with Z at start
  normalizedText = normalizedText.replace(/\b([120])(\d{2}(?:\.\d{1,4})?)\b/g, (match, prefix, rest) => {
    if (prefix === '1') return 'I' + rest;
    if (prefix === '0') return 'O' + rest;
    if (prefix === '2') return 'Z' + rest;
    return match;
  });
  
  // Fix space-separated codes
  normalizedText = normalizedText.replace(/([A-Z])\s+(\d{2}(?:\.\d{1,4})?)/g, '$1$2');
  
  // Fix codes with missing dots: E119 -> E11.9, E103292 -> E10.3292
  normalizedText = normalizedText.replace(/([A-Z])(\d{2})(\d{1,4})\b/g, (match, letter, twoDigits, rest) => {
    if (rest.length <= 4 && !match.includes('.')) {
      return letter + twoDigits + '.' + rest;
    }
    return match;
  });

  const codePatterns = [
    // Pattern 1: Standard ICD-10 format: A00-B99, C00-D49, etc. (chapter ranges)
    // Updated to allow up to 4 digits after decimal (e.g., E10.3292)
    /([A-Z]\d{2}(?:\.\d{1,4})?)/g,
    // Pattern 2: Codes with parentheses: (E11.9), (I10), etc.
    /\(([A-Z]\d{2}(?:\.\d{1,4})?)\)/gi,
    // Pattern 3: Codes with brackets: [E11.9], [I10], etc.
    /\[([A-Z]\d{2}(?:\.\d{1,4})?)\]/gi,
    // Pattern 4: Codes with "ICD-10" or "ICD10" prefix (handles ICD10-M19.011, ICD-10 I10, etc.)
    /ICD[- ]?10[- ]?CM?[- ]?([A-Z]\d{2}(?:\.\d{1,4})?)/gi,
    // Pattern 5: Codes in parentheses with ICD10 prefix: (ICD10-M19.011), (ICD10-I10)
    /\(ICD[- ]?10[- ]?CM?[- ]?-([A-Z]\d{2}(?:\.\d{1,4})?)\)/gi,
    // Pattern 6: Codes after colons: Code: E11.9
    /:\s*([A-Z]\d{2}(?:\.\d{1,4})?)/g,
    // Pattern 7: Codes with spaces: E 11.9, I 10
    /([A-Z])\s+(\d{2}(?:\.\d{1,4})?)/g,
  ];

  // Extract codes using all patterns
  for (const pattern of codePatterns) {
    const matches = normalizedText.matchAll(pattern);
    for (const match of matches) {
      let code = match[1] || match[0];
      // Clean up the code
      code = code.trim().toUpperCase();
      // Remove common prefixes/suffixes
      code = code.replace(/^ICD[- ]?10[- ]?CM?[- ]?/i, '');
      code = code.replace(/[)\]\s]+$/g, '');
      code = code.replace(/^[)\[\s]+/g, '');
      
      // Validate and fix the code
      const validCode = validateAndFixICDCode(code, true);
      if (validCode) {
        codes.add(validCode);
      }
    }
  }

  // Also check for codes in problem list format
  const problemList = extractProblemListFormat(text);
  for (const problem of problemList) {
    if (problem.icd10) {
      const validCode = validateAndFixICDCode(problem.icd10, true);
      if (validCode) {
        codes.add(validCode);
      }
    }
  }

  return Array.from(codes);
}

/**
 * Format ICD codes from text - Extract ONLY ICD-10 codes
 */
export function formatICDCodes(text) {
  text = fixOCRErrorsInCodes(text);
  
  // Extract all ICD-10 codes (comprehensive extraction)
  const allICD10Codes = extractAllICD10Codes(text);
  
  // Create a map to store unique codes
  const codeMap = new Map();
  
  // Add all extracted ICD-10 codes (without descriptions from screenshot)
  for (const code of allICD10Codes) {
    if (!codeMap.has(code)) {
      codeMap.set(code, {
        code: code,
        description: null, // Will be fetched from database
        hcc: false,
        isValidated: false
      });
    }
  }
  
  const uniqueCodes = Array.from(codeMap.values());
  uniqueCodes.sort((a, b) => a.code.localeCompare(b.code));
  
  return uniqueCodes;
}

/**
 * Extract structured medication details from text
 * Returns array of medication objects with name, dosage, form, frequency, route, instructions, and status
 */
export function extractMedicationDetails(text) {
  if (!text || text.trim().length === 0) {
    return [];
  }

  const medications = [];
  const cleanedText = cleanText(text);

  // Map frequency abbreviations to standard format
  const frequencyMap = {
    'qd': 'once a day',
    'od': 'once a day',
    'qday': 'once a day',
    'qid': '4 times a day',
    'tid': '3 times a day',
    'bid': '2 times a day',
    'qhs': 'once a day at bedtime',
    'q hs': 'once a day at bedtime',
    'qam': 'once a day in the morning',
    'qpm': 'once a day in the evening',
    'qod': 'every other day',
    'prn': 'as needed',
    'sos': 'as needed',
    'daily': 'once a day',
    'once daily': 'once a day',
    'twice daily': '2 times a day',
    'three times daily': '3 times a day',
    'four times daily': '4 times a day',
    'q4h': 'every 4 hours',
    'q6h': 'every 6 hours',
    'q8h': 'every 8 hours',
    'q12h': 'every 12 hours'
  };

  // Normalize frequency text
  const normalizeFrequency = (freq) => {
    if (!freq) return '';
    const lower = freq.toLowerCase().trim().replace(/\./g, '');
    return frequencyMap[lower] || freq;
  };

  // Split text into sections (Taking, Not-Taking, Discontinued)
  const sections = {
    'taking': [],
    'not-taking': [],
    'discontinued': []
  };

  // Find section headers - handle both standalone and inline cases
  // Pattern matches: "Taking\n", "Taking ", "Not-Taking\n", etc.
  const sectionPattern = /(?:^|\n)\s*(Taking|Not-Taking|Not Taking|Discontinued)\s*(?:\n|$|\s)/gi;
  let match;

  // Find all section markers
  const sectionMarkers = [];
  while ((match = sectionPattern.exec(cleanedText)) !== null) {
    const sectionName = match[1].toLowerCase().replace(/\s+/g, '-');
    sectionMarkers.push({
      index: match.index,
      section: sectionName,
      text: match[0]
    });
  }

  // Process each section
  if (sectionMarkers.length === 0) {
    // No section markers, check if text starts with "Current Medications" or similar
    // and treat everything after as "Taking"
    const headerMatch = cleanedText.match(/^(?:Current\s+)?Medications?\s*(?:Taking)?\s*/i);
    if (headerMatch) {
      const medicationText = cleanedText.substring(headerMatch[0].length).trim();
      if (medicationText) {
        sections.taking = [medicationText];
      } else {
        sections.taking = [cleanedText];
      }
    } else {
      sections.taking = [cleanedText];
    }
  } else {
    // Also handle text before first section marker
    if (sectionMarkers[0].index > 0) {
      const beforeFirstSection = cleanedText.substring(0, sectionMarkers[0].index).trim();
      // Remove "Current Medications" header if present
      const headerMatch = beforeFirstSection.match(/^(?:Current\s+)?Medications?\s*(?:Taking)?\s*/i);
      if (headerMatch) {
        const medicationText = beforeFirstSection.substring(headerMatch[0].length).trim();
        if (medicationText) {
          sections.taking.push(medicationText);
        }
      } else if (beforeFirstSection && !/^(?:Current\s+)?Medications?/i.test(beforeFirstSection)) {
        sections.taking.push(beforeFirstSection);
      }
    }
    
    for (let i = 0; i < sectionMarkers.length; i++) {
      const startIndex = sectionMarkers[i].index + sectionMarkers[i].text.length;
      const endIndex = i < sectionMarkers.length - 1 
        ? sectionMarkers[i + 1].index 
        : cleanedText.length;
      let sectionText = cleanedText.substring(startIndex, endIndex).trim();
      
      // Remove "Current Medications" header if present in section text
      const headerMatch = sectionText.match(/^(?:Current\s+)?Medications?\s*(?:Taking)?\s*/i);
      if (headerMatch) {
        sectionText = sectionText.substring(headerMatch[0].length).trim();
      }
      
      const sectionKey = sectionMarkers[i].section;
      if (sections[sectionKey] && sectionText) {
        sections[sectionKey].push(sectionText);
      }
    }
  }

  // Process each section
  for (const [status, sectionTexts] of Object.entries(sections)) {
    for (const sectionText of sectionTexts) {
      // Split by lines
      const lines = sectionText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      
      for (const line of lines) {
        // Skip section headers and empty lines
        if (/^(Taking|Not-Taking|Not Taking|Discontinued)$/i.test(line)) {
          continue;
        }

        // Skip lines that are clearly not medications
        if (/^(Medication|Current|List|reviewed|reconciled|Medication List)/i.test(line)) {
          continue;
        }
        
        // Skip lines that are just "Taking" or section markers
        if (/^\s*(Taking|Not-Taking|Not Taking|Discontinued)\s*$/i.test(line)) {
          continue;
        }

        // Parse medication line
        // Pattern: medication name [dosage] [form] [quantity] [route] [frequency] [instructions]
        // Example: "metoprolol 25 mg tablet, extended release 1 tab(s) orally once a day"
        
        let medicationName = '';
        let dosage = '';
        let form = '';
        let quantity = '';
        let route = '';
        let frequency = '';
        let instructions = '';
        let notes = '';

        // Remove "Taking" keyword if it appears at the start of the line
        let processedLine = line.replace(/^Taking\s+/i, '').trim();
        // Normalize thousands separators to aid parsing (e.g., "1,000 mg")
        processedLine = processedLine.replace(/(\d),(?=\d{3}\b)/g, '$1');
        // Remove inline DX annotations that can confuse strength parsing
        processedLine = processedLine.replace(/\bDX[:.]?\s*[A-Za-z0-9.\-]+\b/gi, '').trim();
        
        // Extract medication name (usually first word or words before dosage)
        // Medication name can be multiple words (e.g., "Metoprolol Succinate ER", "Entresto", "Vitamin B-12", "metoprolol")
        // Handle both capitalized and lowercase medication names
        let nameMatch = processedLine.match(/^([A-Z][a-zA-Z\s\-]+?)(?:\s+\d|$)/);
        if (!nameMatch) {
          // Try lowercase medication names (e.g., "metoprolol 25 mg")
          nameMatch = processedLine.match(/^([a-z][a-zA-Z\s\-]+?)(?:\s+\d)/);
        }
        if (nameMatch) {
          medicationName = nameMatch[1].trim();
        } else {
          // Fallback: extract words until we hit a dosage pattern or form
          const words = processedLine.split(/\s+/);
          const nameWords = [];
          const stopWords = /^(mg|mcg|units?|%|tab|cap|tablet|capsule|injection|cream|ointment|gel|solution|suspension|drops|spray|patch|orally|subcutaneously|inhaled|topically|intranasally|once|twice|daily|BID|TID|QID|QD|QHS|QAM|PRN|Taking|Not-Taking|Discontinued)$/i;
          
          for (let i = 0; i < words.length; i++) {
            const word = words[i];
            // Stop if we hit a dosage number or stop word
            if (/^\d+(?:\.\d+)?/.test(word) && (word.includes('mg') || word.includes('mcg') || word.includes('unit'))) {
              break;
            }
            if (stopWords.test(word)) {
              break;
            }
            // Include the word if it looks like part of a medication name
            if (/^[A-Za-z]/.test(word) || word.includes('-')) {
              nameWords.push(word);
            } else if (nameWords.length > 0) {
              // If we've started collecting name words, stop at non-name characters
              break;
            }
          }
          medicationName = nameWords.join(' ').trim();
        }
        
        // Clean up medication name - remove trailing common words that aren't part of the name
        medicationName = medicationName.replace(/\s+(tablet|capsule|injection|cream|ointment|gel|solution|suspension|drops|spray|patch|powder|syrup|soap|film|pen|disk|diskus|ellipta|inhaler|nebulizer|liquid)$/i, '');
        medicationName = medicationName.replace(/[-\s]+$/, '');

        // Extract dosage / strength (includes concentrations and ranges)
        const strengthParts = [];
        const addStrength = (value) => {
          if (value) {
            const trimmed = value.trim();
            if (trimmed && !strengthParts.includes(trimmed)) {
              strengthParts.push(trimmed);
            }
          }
        };

        // Multi-strength chains like "100 mcg-62.5 mcg-25 mcg/inh"
        const multiStrengthChain = /\d+(?:\.\d+)?\s*(?:mg|mcg|g|units?|mEq|intl\s+units?)(?:\/\s*(?:mL|ml|l|g|mcg|mg|inh|puff|actuation))?(?:\s*-\s*\d+(?:\.\d+)?\s*(?:mg|mcg|g|units?|mEq|intl\s+units?)(?:\/\s*(?:mL|ml|l|g|mcg|mg|inh|puff|actuation))?)+/gi;
        const multiStrengthMatch = processedLine.match(multiStrengthChain);
        if (multiStrengthMatch) {
          multiStrengthMatch.forEach(addStrength);
        }

        // Ranges like "24 mg-26 mg"
        const dosageRangePattern = /(\d+(?:\.\d+)?\s*(?:mg|mcg|units?|%|g|mEq|intl\s+units?)\s*-\s*\d+(?:\.\d+)?\s*(?:mg|mcg|units?|%|g|mEq|intl\s+units?))/gi;
        const dosageRangeMatch = processedLine.match(dosageRangePattern);
        if (dosageRangeMatch) {
          dosageRangeMatch.forEach(addStrength);
        }

        // Concentrations like "100 units/mL", "0.5 mg/2 mL", "0.005% solution", "1 mg/g", "0.05%-1% cream", "200/mL"
        const concentrationPatterns = [
          /\d+(?:\.\d+)?\s*%-\s*\d+(?:\.\d+)?\s*%(?:\s*(?:solution|cream|ointment|gel|lotion|spray|suspension|shampoo|foam|drops?)\b)?/gi,
          /\d+(?:\.\d+)?\s*(?:mg|mcg|g|units?|mEq|intl\s+units?)\s*\/\s*\d+(?:\.\d+)?\s*(?:mL|ml|l|g|mcg|mg|units?|actuation|spray|inh|puff|drop|gtt)\b/gi,
          /\d+(?:\.\d+)?\s*\/\s*mL\b/gi,
          /\d+(?:\.\d+)?\s*%\s*(?:solution|cream|ointment|gel|lotion|spray|suspension|shampoo|foam|drops?)?/gi,
          /\d+(?:\.\d+)?\s*(?:mcg|mg|g)\s*\/\s*(?:actuation|spray|inh|puff)\b/gi
        ];
        concentrationPatterns.forEach((pattern) => {
          const matches = processedLine.match(pattern);
          if (matches) {
            matches.forEach(addStrength);
          }
        });

        // Parenthetical alternate strengths "(0.05 mg)"
        const parenStrengthPattern = /\((\d+(?:\.\d+)?\s*(?:mg|mcg|g|units?|mEq|intl\s+units?|%))\)/gi;
        const parenMatch = processedLine.match(parenStrengthPattern);
        if (parenMatch) {
          parenMatch.forEach((m) => {
            addStrength(m.replace(/[()]/g, ''));
          });
        }

        // Simple strengths like "25 mg", "500 mcg", "40 mg" (ensure we don't misread "gtt" as "g")
        const simpleDosagePattern = /\b\d+(?:\.\d+)?\s*(?:mg|mcg|units?|%|g|mEq|intl\s+units?)\b/gi;
        const simpleDosageMatch = processedLine.match(simpleDosagePattern);
        if (simpleDosageMatch) {
          simpleDosageMatch.forEach(addStrength);
        }

        if (strengthParts.length > 0) {
          // Keep distinct strength parts, prefer longer strings first and drop substrings to reduce duplication
          const deduped = [];
          const sorted = strengthParts.sort((a, b) => b.length - a.length);
          for (const s of sorted) {
            if (!deduped.some(existing => existing.includes(s))) {
              deduped.push(s);
            }
          }
          dosage = deduped.reverse().join(', ');
        }

        // Extract form (tablet, capsule, solution, etc.) including modifiers like "extended release"
        // Handle patterns like "tablet, extended release" or "capsule, delayed release"
        const formWithModifierPattern = /(tablet|capsule|injection|cream|ointment|gel|solution|suspension|drops|spray|patch|powder|syrup|soap|film|pen|disk|diskus|ellipta|inhaler|nebulizer|liquid)(?:\s*,\s*(extended\s+release|delayed\s+release|enteric\s+coated|ER|DR|XR|extended\s+release\s+tablet|delayed\s+release\s+tablet|enteric\s+coated\s+tablet))?/gi;
        const formWithModifierMatch = processedLine.match(formWithModifierPattern);
        if (formWithModifierMatch) {
          form = formWithModifierMatch[0].trim();
        } else {
          // Fallback: just extract the form without modifier
          const formPattern = /(tablet|capsule|injection|cream|ointment|gel|solution|suspension|drops|spray|patch|powder|syrup|soap|film|pen|disk|diskus|ellipta|inhaler|nebulizer|liquid)/gi;
          const formMatch = processedLine.match(formPattern);
          if (formMatch) {
            form = formMatch[0].trim();
          }
        }

        // Extract quantity (e.g., "1 tab(s)", "1/2 cap(s)", "2 tablets", "30 units")
        const quantityPattern = /(\d+(?:\/\d+)?(?:\.\d+)?)[\s-]*(?:tab|caps?|tablet|capsule|puff|spray|gtt|drop|patch|app|application|vial|unit|ml|mL|g|mg|mcg|intl\s+units?|dose|doses?)(?:\(s\))?/gi;
        const quantityMatch = processedLine.match(quantityPattern);
        if (quantityMatch) {
          quantity = quantityMatch[0].trim();
        } else {
          // Fallback for "1 gtt in each eye" style phrases
          const gttFallback = processedLine.match(/(\d+(?:\/\d+)?(?:\.\d+)?)\s*gtt\b/gi);
          if (gttFallback) {
            quantity = gttFallback[0].trim();
          } else {
            // Fallback for bare numeric dose preceding SUBQ/SQ/SC/IM/IV
            const numericDose = processedLine.match(/(\d+(?:\/\d+)?(?:\.\d+)?)(?=\s*(?:subq|subcut|sq|sc|im|iv|q\s*hs|qhs)\b)/i);
            if (numericDose) {
              quantity = numericDose[1].trim();
            }
          }
        }

        // Extract route (orally, subcutaneously, inhaled, topically, intranasally, etc.)
        const routePattern = /(orally|by\s+mouth|oral|subcutaneously|subcutaneous|subq|sq|sc|inhaled|topically|intranasally|nasally|applied\s+topically|in\s+each\s+eye|each\s+eye|both\s+eyes|each\s+affected\s+eye|by\s+nebulizer|sublingually|IM|intramuscularly|IV|intravenously|ophthalmic|otic|inh\b)/gi;
        const routeMatch = processedLine.match(routePattern);
        if (routeMatch) {
          route = routeMatch[0].trim();
        }

        // Extract frequency (once a day, 2 times a day, BID, TID, QD, QHS, PRN, etc.)
        const frequencyPattern = /(\d+\s+times?\s+(?:a\s+)?day|once\s+(?:a\s+)?day|twice\s+(?:a\s+)?day|daily|once\s+daily|BID|TID|QID|QD|QHS|Q\s*HS|QAM|QPM|QOD|PRN|SOS|q\d+\s*(?:h|hr|hrs|hour|hours)|every\s+\d+\s+hours?|every\s+\d+\s+days?|before\s+each\s+meal|before\s+meals)/gi;
        const frequencyMatch = processedLine.match(frequencyPattern);
        if (frequencyMatch) {
          frequency = normalizeFrequency(frequencyMatch[0].trim());
        }

        // Extract instructions (at bedtime, in the morning, in the evening, fasting, etc.)
        const instructionPattern = /\((?:at\s+bedtime|in\s+the\s+morning|in\s+the\s+evening|fasting|by\s+itself|in\s+empty\s+stomach|as\s+directed|for\s+[^)]+)\)/gi;
        const instructionMatch = processedLine.match(instructionPattern);
        if (instructionMatch) {
          instructions = instructionMatch.map(m => m.replace(/[()]/g, '')).join(', ');
        }
        // Instruction keywords outside parentheses
        const instructionKeywords = [
          'fasting',
          'by itself',
          'in empty stomach',
          'empty stomach',
          'with food',
          'with meals',
          'before each meal',
          'before meals',
          'with breakfast',
          'with dinner',
          'with lunch',
          'at bedtime',
          'in the morning',
          'in the evening',
          'before bed',
          'as directed',
          'x 2 doses',
          '2-6 months apart',
          'before each meal'
        ];
        const instructionRegex = new RegExp(`\\b(${instructionKeywords.join('|')})\\b`, 'gi');
        const inlineInstructions = processedLine.match(instructionRegex);
        if (inlineInstructions) {
          const cleanInline = inlineInstructions.map(i => i.trim());
          const merged = instructions ? instructions.split(', ').concat(cleanInline) : cleanInline;
          // Deduplicate while preserving order
          const seenInstr = new Set();
          instructions = merged.filter(x => {
            const low = x.toLowerCase();
            if (seenInstr.has(low)) return false;
            seenInstr.add(low);
            return true;
          }).join(', ');
        }

        // Extract notes (after "Notes:" or "DX:" or similar)
        const notesPattern = /(?:Notes?|DX|Diagnosis|Dx):\s*([^,]+(?:,\s*[^,]+)*)/gi;
        const notesMatch = processedLine.match(notesPattern);
        if (notesMatch) {
          notes = notesMatch.map(m => m.replace(/^(?:Notes?|DX|Diagnosis|Dx):\s*/i, '')).join(', ');
        }

        // Extract PRN if present
        if (/prn/i.test(processedLine) && !frequency.toLowerCase().includes('prn')) {
          if (instructions) {
            instructions += ', PRN';
          } else {
            instructions = 'PRN';
          }
        }

        // Only add if we have at least a medication name
        if (medicationName && medicationName.length >= 2) {
          medications.push({
            name: medicationName.trim(),
            dosage: dosage.trim(),
            form: form.trim(),
            quantity: quantity.trim(),
            route: route.trim(),
            frequency: frequency.trim(),
            instructions: instructions.trim(),
            notes: notes.trim(),
            status: status === 'not-taking' ? 'not-taking' : status === 'discontinued' ? 'discontinued' : 'taking'
          });
        }
      }
    }
  }

  return medications;
}

/**
 * Extract medication names from text (legacy function for backward compatibility)
 */
export function extractMedications(text) {
  if (!text || text.trim().length === 0) {
    return [];
  }

  // Use the new detailed extraction and return just names
  const detailedMedications = extractMedicationDetails(text);
  return detailedMedications.map(med => med.name);
}
